import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { FaEdit, FaEye, FaTrashAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../Styles/yellowcalfcss/ExpensesDashboard.css';
import ApiConfig from '../../api/ApiConfig';
import { simplePostCall } from '../../api/ApiServices';
import ScaleLoader from 'react-spinners/ScaleLoader';

export default function ExpensesDashboard() {
  const navigate = useNavigate();
  
  // States
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [expenseNumberFilter, setExpenseNumberFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');

  /**
   * 1. API Integration Function
   */
  const fetchExpensesList = useCallback(async () => {
    setLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
      customer_id: Number(customer_id) || 1609,
      api_key: api_key || "8440d41adb8335314263dc31d4504cf7",
      user_id: Number(user_id) || 56950,
      language: "en",
      amount_search: "", 
      type_search: accountTypeFilter,  // Link to filter state
      number_search: expenseNumberFilter, // Link to filter state
      limit: 100,
      page: 1
    };

    try {
      const response = await simplePostCall(ApiConfig.expensesList, JSON.stringify(requestBody));

      if (response?.result) {
        // Update local state with API data
        setExpenses(response.data || []);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error("Failed to load expenses list");
    } finally {
      setLoading(false);
    }
  }, [expenseNumberFilter, accountTypeFilter]); // Re-run when filters change

  // 2. Initial Fetch
  useEffect(() => {
    fetchExpensesList();
  }, [fetchExpensesList]);

  // 3. Delete Handler
const handleDelete = async (id) => {
    // 1. Confirm with user before deleting
    if (!window.confirm("Are you sure you want to delete this expense?")) return;

    setLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
        customer_id: Number(customer_id) || 1609,
        api_key: api_key || "8440d41adb8335314263dc31d4504cf7",
        user_id: Number(user_id) || 56950,
        language: "en",
        id: id // The ID of the expense to delete
    };

    try {
        const response = await simplePostCall(ApiConfig.expensesListDelete, JSON.stringify(requestBody));

        if (response?.result) {
            toast.success("Expense deleted successfully");
            
            // 2. Refresh the list after successful deletion
            fetchExpensesList(); 
        } else {
            toast.error(response?.message || "Failed to delete expense");
        }
    } catch (error) {
        console.error("Error deleting expense:", error);
        toast.error("An error occurred during deletion");
    } finally {
        setLoading(false);
    }
};

  return (
    <div className="expdash-outer-wrap">
      <ToastContainer />
      <Container fluid className="expdash-inner-wrap">

        {/* HEADER */}
        <div className="expdash-header-section" style={{ height: '60px'}}>
          <h3>Expenses</h3>
          <div className="expdash-btn-group">
            <Button className="expdash-action-btn" onClick={() => navigate("/accountant/Type")}>
              Account Type
            </Button>
            <Button className="expdash-action-btn" onClick={() => navigate("/accountant/ExpenseType")}>
              Expense Type
            </Button>
            {/* <Button className="expdash-action-btn" onClick={() => navigate("/accountant/IncomeType")}>
              Income Type
            </Button> */}
             <Button className="expdash-action-btn" onClick={() => navigate("/accountant/createExpenses")}>
              Create Expense
            </Button>
            <Button className="expdash-action-btn">
              Export
            </Button>
          </div>
        </div>

        {/* FILTER SECTION */}
        <div className="expdash-filter-section">
          <div className="expdash-filter-inputs" style={{ height: '37px', marginTop: '-10px', width: '500px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', top: '45%', transform: 'translateY(-50%)', color: '#838080' }} />
              <Form.Control
                type="text"
                placeholder="Filter by Expense Number"
                value={expenseNumberFilter}
                onChange={(e) => setExpenseNumberFilter(e.target.value)}
                className="expdash-filter-field"
                style={{ paddingLeft: '32px' }}
              />
            </div>

            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', top: '45%', transform: 'translateY(-50%)', color: '#878080' }} />
              <Form.Control
                type="text"
                placeholder="Filter by Account Type"
                value={accountTypeFilter}
                onChange={(e) => setAccountTypeFilter(e.target.value)}
                className="expdash-filter-field"
                style={{ paddingLeft: '32px' }}
              />
            </div>
          </div>
        </div>

        {/* TABLE HEADER */}
        <div className="expdash-table-header" style={{ marginTop: '4px' }}>
          <div className="expdash-row-layout" style={{  height: '38px', fontWeight: 'bold' }}>
            <div className="expdash-col-serial" style={{ width: '50px' }}>Sr.No</div>
            <div className="expdash-col-expense-num">Expenses Number</div>
            <div className="expdash-col-account-type">Account Type</div>
            <div className="expdash-col-total-amount">Total Amount</div>
            <div className="expdash-col-actions" style={{ marginLeft: '70px' }}>Actions</div>
          </div>
        </div>

        {/* RECORDS */}
        <div className="expdash-records-container">
          {loading ? (
            <div className="text-center p-2"> <ScaleLoader color="#F4BB44" /></div>
          ) : expenses.length > 0 ? (
            expenses.map((expense, index) => (
              <div key={expense.expenses_id} className="expdash-record-item" >
                <div className="expdash-row-layout" >
                  <div className="expdash-col-serial" style={{ width: '50px' }}>{index + 1}</div>
                  <div className="expdash-col-expense-num" style={{marginLeft: '20px'}}>{expense.expenses_number}</div>
                  <div className="expdash-col-account-type" style={{marginLeft: '20px'}}>{expense.expense_account_type_name}</div>
                  <div className="expdash-col-total-amount"style={{marginLeft: '20px'}}><b>{expense.expenses_total_amount}</b></div>
                  <div className="expdash-col-actions" style={{ marginLeft: '55px', display: 'flex', gap: '1px' }}>
                    <button className="expdash-control-btn" onClick={() => navigate(`/ExpenseViewNew/${expense.expenses_id}`)}>
                      <FaEye />
                    </button>
                    <button className="expdash-control-btn" onClick={() => navigate(`/accountant/createExpenses/${expense.expenses_id}`)}>
                      <FaEdit />
                    </button>
                    <button className="expdash-control-btn" onClick={() => handleDelete(expense.expenses_id)}>
                      <FaTrashAlt color="red" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center p-4">No records found.</div>
          )}
        </div>

        {/* SUMMARY */}
        <p className="expdash-summary-text" style={{ marginTop: '20px' }}>
          Showing {expenses.length} items
        </p>

      </Container>
    </div>
  );
}